import { User as UserIcon, Mail, MapPin, Calendar, Edit, Save, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button  } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useState, useEffect ,useRef} from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radioGroup";
import { User } from "../types";
import { UserRepository } from '../infrastructure/repositories/UserRepository';
import { SharetribeRepository } from '../infrastructure/repositories/SharetribeRepository';
import { apiService } from '@/services';
import { toast } from "sonner";
import { secureStorage } from "@/utils/secureStorage";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export function MyProfileContent() {
  const normalizeGenderForForm = (value?: string) => {
    if (!value) return "";
    const normalized = value.toLowerCase();
    if (normalized === "m" || normalized === "male") return "male";
    if (normalized === "f" || normalized === "female") return "female";
    return "";
  };

  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    fname: '',
    lname: '',
    email: '',
    flex_id: '',
    dob: '',
    gender: '',
    lang: 'en',
    height: '',
    weight: ''
  });
  const [isSaving, setIsSaving] = useState(false);
const [validationErrors, setValidationErrors] = useState<{
  fname?: string;
  lname?: string;
  email?: string;
  dob?: string;
  gender?: string;
  lang?: string;
  height?: string;
  weight?: string;
}>({});

useEffect(() => {
  const loadUserProfile = async () => {
    try {
      setLoading(true);

      const userId =
        Number(localStorage.getItem("current_user_swimmer_id")) ||
        Number(localStorage.getItem("user_id"));

      if (!userId) {
        throw new Error("User ID not found");
      }

      const userRepository = new UserRepository();

      const response = await userRepository.getUserById(userId);

      if (!response || response.status !== 1 || !response.data) {
        throw new Error("Invalid profile response");
      }

      const data = response.data;

      const normalizedProfile: User = {
        id: Number(data.id),
        email: data.email || "",
        fname: data.fname || "",
        lname: data.lname || "",
        flex_id: data.flex_id || "",
        dob: data.dob ? data.dob.split(" ")[0] : "",
        gender: normalizeGenderForForm(data.gender),
        lang: data.lang || "en",
        height: data.height ? Number(data.height) : undefined,
        weight: data.weight ? Number(data.weight) : undefined,
        image:
          data.image ||
          secureStorage.get("current_user_pic") ||
          "",
      };

      setUserProfile(normalizedProfile);

      setEditForm({
          fname: normalizedProfile.fname ?? "",
          lname: normalizedProfile.lname ?? "",
          email: normalizedProfile.email ?? "",
          flex_id: normalizedProfile.flex_id ?? "",
          dob: normalizedProfile.dob ?? "",
          gender: normalizeGenderForForm(normalizedProfile.gender),
          lang: normalizedProfile.lang ?? "en",
          height: normalizedProfile.height?.toString() ?? "",
          weight: normalizedProfile.weight?.toString() ?? "",
        });

    //  localStorage.setItem("user_data", JSON.stringify(normalizedProfile));
      secureStorage.set( 
        "current_user_name",
        `${normalizedProfile.fname} ${normalizedProfile.lname}`
      );

    } catch (error) {
      console.error("Failed to load profile from API, using fallback", error);

      const cached = localStorage.getItem("user_data");
      if (cached) {
        const parsed = JSON.parse(cached);
        setUserProfile(parsed);

        setEditForm({
          fname: parsed.fname || "",
          lname: parsed.lname || "",
          email: parsed.email || "",
          flex_id: parsed.flex_id || "",
          dob: parsed.dob || "",
          gender: normalizeGenderForForm(parsed.gender),
          lang: parsed.lang || "en",
          height: parsed.height?.toString() || "",
          weight: parsed.weight?.toString() || "",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  loadUserProfile();
}, []);



  const openEditDialog = () => {
    if (userProfile) {
      setEditForm({
        fname: userProfile.fname || '',
        lname: userProfile.lname || '',
        email: userProfile.email || '',
        flex_id: userProfile.flex_id || '',
        dob: userProfile.dob || '',
       gender: normalizeGenderForForm(userProfile.gender),
        lang: userProfile.lang || 'en',
        height: userProfile.height?.toString() || '',
        weight: userProfile.weight?.toString() || ''
      });
      setValidationErrors({}); // Clear validation errors when opening dialog
      setIsEditOpen(true);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isFutureDate = (date: string) =>
  new Date(date) > new Date();

  const handleSaveProfile = async () => {
  if (!userProfile) return;

  // Validate required fields
 const errors: typeof validationErrors = {};

// First & Last name
if (!editForm.fname.trim()) {
  errors.fname = "First name is required";
}

if (!editForm.lname.trim()) {
  errors.lname = "Last name is required";
}

// Email (optional but must be valid if present)
if (editForm.email && !isValidEmail(editForm.email)) {
  errors.email = "Invalid email address";
}

// DOB
if (editForm.dob && isFutureDate(editForm.dob)) {
  errors.dob = "Date of birth cannot be in the future";
}

// Gender
if (!editForm.gender) {
  errors.gender = "Please select gender";
}

// Language
if (!["en", "fr"].includes(editForm.lang)) {
  errors.lang = "Please select a valid language";
}

// Height
if (editForm.height) {
  const h = Number(editForm.height);
  if (isNaN(h) || h < 50 || h > 300) {
    errors.height = "Height must be between 50 and 300 cm";
  }
}

// Weight
if (editForm.weight) {
  const w = Number(editForm.weight);
  if (isNaN(w) || w < 20 || w > 300) {
    errors.weight = "Weight must be between 20 and 300 kg";
  }
}

if (Object.keys(errors).length > 0) {
  setValidationErrors(errors);
  toast.error("Please fix the highlighted fields");
  return;
}

setValidationErrors({});


  setIsSaving(true);
  try {
    const userRepository = new UserRepository();
const updatedParams = {
  id: userProfile.id!,
  dob: editForm.dob,
  gender: editForm.gender === "male" ? "M" : "F",
  height: Number(editForm.height),
  weight: Number(editForm.weight),
  lang: editForm.lang || "en",
  fname: editForm.fname.trim(),
  lname: editForm.lname.trim(),
  image: userProfile.image || "",
  third_party_swimmer_id:
    localStorage.getItem("current_user_swimmer_id") || "",
};



    console.log('user id' , userProfile.id);

    const updatedDomainUser = await userRepository.updateProfile(updatedParams);

    // Convert domain user to UI user
    const updatedUser: User = {
      id: parseInt(updatedDomainUser.id),
      email: updatedDomainUser.email,
      fname: updatedDomainUser.firstName,
      lname: updatedDomainUser.lastName,
      flex_id: userProfile.flex_id,
      dob: updatedParams.dob,
      gender: updatedParams.gender,
      lang: updatedParams.lang,
      height: updatedParams.height,
      weight: updatedParams.weight,
      image: userProfile.image,
    };


    setUserProfile(updatedUser);

    secureStorage.set('current_user_name', `${updatedUser.fname} ${updatedUser.lname}`);

    setIsEditOpen(false);
  } catch (error) {
    console.error('Error saving profile:', error);
  } finally {
    setIsSaving(false);
  }
};
const fileInputRef = useRef<HTMLInputElement | null>(null);

const handleAvatarClick = () => {
  fileInputRef.current?.click();
};

const handleProfileImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file || !userProfile) return;

  try {
    const sharetribeRepo = new SharetribeRepository(apiService);

    const uploadResponse = await sharetribeRepo.uploadImage(file, file.type);

    if (!uploadResponse?.data?.id) {
      alert("Image upload failed.");
      return;
    }

    const profileImageId = uploadResponse.data.id;

     await sharetribeRepo.updateProfile(
      null, null, null, null,
      null, null, null,
      profileImageId
    );

    const newImageUrl = uploadResponse.data.attributes.variants.default?.url;

    if (!newImageUrl) {
      console.warn("No URL returned from Sharetribe.");
      return;
    }

    secureStorage.set("current_user_pic", newImageUrl);

    // Update the server with the new image URL using UserRepository
    const userRepository = new UserRepository();
    const updatedParams = {
      id: userProfile.id!,
      dob: userProfile.dob || "2005-05-20", 
      gender: userProfile.gender || "M",
      height: userProfile.height || 165,
      weight: userProfile.weight || 58,
      lang: userProfile.lang || "en",
      fname: userProfile.fname || '',
      lname: userProfile.lname || '',
      image: newImageUrl,
      third_party_swimmer_id: localStorage.getItem('current_user_swimmer_id') || '',
    };

    await userRepository.updateProfile(updatedParams);

    setUserProfile(prev =>
      prev ? { ...prev, image: newImageUrl } : prev
    );

    // Update localStorage user_data as well
    const userData = localStorage.getItem('user_data');
    if (userData) {
      const parsed = JSON.parse(userData);
      localStorage.setItem('user_data', JSON.stringify({
        ...parsed,
        image: newImageUrl
      }));
    }

  } catch (error) {
    console.error("Error updating profile image:", error);
    alert("Failed to update profile image on server.");
  }
};

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="text-center py-8">
              <h2 className="text-xl font-semibold text-gray-600">No Profile Data Found</h2>
              <p className="text-muted-foreground mt-2">Please log in again to load your profile information.</p>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const fullName = `${userProfile.fname || ''} ${userProfile.lname || ''}`.trim() || 'User';
  const initials = `${userProfile.fname?.[0] || ''}${userProfile.lname?.[0] || ''}`.toUpperCase() || 'U';
  const coachId = localStorage.getItem('coach_id');
  const userType = coachId ? 'Coach' : 'Swimmer';

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
          <div className="relative">
            <Avatar
              className="h-20 w-20 cursor-pointer hover:opacity-80 transition"
              onClick={handleAvatarClick}
            >
              <AvatarImage src={userProfile?.image || undefined} />
              <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
            </Avatar>

            {/* Hidden file input */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              className="hidden"
              onChange={handleProfileImageChange}
            />
          </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold">{fullName}</h1>
              <p className="text-muted-foreground">{userType}</p>
              <div className="flex items-center space-x-2 mt-2">
                <Badge variant="secondary">Active</Badge>
              </div>
            </div>
            <Button variant="outline" onClick={openEditDialog}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Profile
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Personal Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <UserIcon className="mr-2 h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{userProfile.email || 'Not provided'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <span>ID: {userProfile.id || 'Not available'}</span>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>Type: {userType}</span>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>
                {userProfile.dob 
                  ? `Born: ${new Date(userProfile.dob).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`
                  : 'Member since recently'
                }
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{userType} Statistics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">User ID</span>
              <span className="font-semibold text-sm">{userProfile.id || 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Account Type</span>
              <span className="font-semibold">{userType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gender</span>
              <span className="font-semibold">{userProfile.gender || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Language</span>
              <span className="font-semibold text-sm">{userProfile.lang || 'Not specified'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="flex-1">
                <p className="font-medium">Profile Status</p>
                <p className="text-sm text-muted-foreground">Profile loaded from local storage</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <div className="flex-1">
                <p className="font-medium">Authentication</p>
                <p className="text-sm text-muted-foreground">Logged in successfully</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <div className="flex-1">
                <p className="font-medium">Data Source</p>
                <p className="text-sm text-muted-foreground">
                  {userProfile.email ? 'Full profile data available' : 'Basic profile data from fallback'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Edit className="mr-2 h-5 w-5" />
              Edit Profile
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4 overflow-visible">
            
            {/* Basic Information */}
            <div className="space-y-4 overflow-visible">
              <h3 className="text-lg font-semibold">Basic Information</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
                <div className="space-y-2">
                  <Label htmlFor="fname">First Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="fname"
                    value={editForm.fname}
                    onChange={(e) => {
                      handleInputChange('fname', e.target.value);
                      // Clear error when user starts typing
                      if (validationErrors.fname) {
                        setValidationErrors(prev => ({ ...prev, fname: undefined }));
                      }
                    }}
                    placeholder="Enter first name"
                    className={validationErrors.fname ? "border-red-500" : ""}
                  />
                  {validationErrors.fname && (
                    <p className="text-sm text-red-500">{validationErrors.fname}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lname">Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="lname"
                    value={editForm.lname}
                    onChange={(e) => {
                      handleInputChange('lname', e.target.value);
                      // Clear error when user starts typing
                      if (validationErrors.lname) {
                        setValidationErrors(prev => ({ ...prev, lname: undefined }));
                      }
                    }}
                    placeholder="Enter last name"
                    className={validationErrors.lname ? "border-red-500" : ""}
                  />
                  {validationErrors.lname && (
                    <p className="text-sm text-red-500">{validationErrors.lname}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={validationErrors.email ? "border-red-500" : ""}
                  />
                  {validationErrors.email && (
                    <p className="text-sm text-red-500">{validationErrors.email}</p>
                  )}

                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="space-y-4 overflow-visible">
              <h3 className="text-lg font-semibold">Personal Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">

                <div className="space-y-2">
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                      id="dob"
                      type="date"
                      value={editForm.dob}
                      onChange={(e) => handleInputChange("dob", e.target.value)}
                      className={validationErrors.dob ? "border-red-500" : ""}
                    />
                    {validationErrors.dob && (
                      <p className="text-sm text-red-500">{validationErrors.dob}</p>
                    )}

                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <div className="rounded-lg border bg-white dark:bg-slate-900 px-4 py-3">
                    <RadioGroup
                      value={editForm.gender}
                      onValueChange={(val) => handleInputChange("gender", val)}
                      className="flex gap-2"
                    >
                      {[
                        { value: "male", label: "Male" },
                        { value: "female", label: "Female" }
                      ].map((option) => (
                        <Label
                          key={option.value}
                          htmlFor={`gender-${option.value}`}
                          className="inline-flex items-center gap-2 px-2 py-1 cursor-pointer text-sm font-medium"
                        >
                          <RadioGroupItem id={`gender-${option.value}`} value={option.value} />
                          {option.label}
                        </Label>
                      ))}
                    </RadioGroup>

                    {validationErrors.gender && (
                      <p className="text-sm text-red-500 mt-1">
                        {validationErrors.gender}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 overflow-visible">
                  <Label htmlFor="lang" >Language</Label>

                  <Select
                    value={editForm.lang}
                    onValueChange={(val) => handleInputChange("lang", val)}
                  >
                    <SelectTrigger
                      id="lang"
                      className="w-full bg-white text-foreground dark:bg-slate-900"
                    >
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>

                    <SelectContent className="z-[9999]">
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                    </SelectContent>
                  </Select>

                  {validationErrors.lang && (
                    <p className="text-sm text-red-500 mt-1">
                      {validationErrors.lang}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    value={editForm.height}
                    min={1}
                    onChange={(e) => handleInputChange('height', e.target.value)}
                    placeholder="Enter height"
                  />
                  {validationErrors.height && (
                    <p className="text-sm text-red-500">{validationErrors.height}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                      min={1}
                    value={editForm.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    placeholder="Enter weight"
                  />
                  {validationErrors.weight && (
                    <p className="text-sm text-red-500">{validationErrors.weight}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsEditOpen(false)}
                disabled={isSaving}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>

              <Button onClick={handleSaveProfile} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
